<template>
    <jet-action-section>
        <template #title>
            Browser Sessions
        </template>

        <template #description>
            Manage and logout your active sessions on other browsers and devices.
        </template>

        <template #content>
            <div class="max-w-xl text-sm text-gray-600">
                If necessary, you may logout of all of your other browser sessions across all of your devices. Some of your recent sessions are listed below; however, this list may not be exhaustive. If you feel your account has been compromised, you should also update your password.
            </div>

            <!-- Other Browser Sessions -->
            <div class="mt-5 space-y-6" v-if="safeSessions.length > 0">
                <div class="flex items-center" v-for="(session, i) in safeSessions" :key="i">
                    <div>
                        <svg fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24" stroke="currentColor" class="w-8 h-8 text-gray-500" v-if="session.agent.is_desktop">
                            <path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                        </svg>

                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8 text-gray-500" v-else>
                            <path d="M0 0h24v24H0z" stroke="none"></path><rect x="7" y="4" width="10" height="16" rx="1"></rect><path d="M11 5h2M12 17v.01"></path>
                        </svg>
                    </div>

                    <div class="ml-3">
                        <div class="text-sm text-gray-600">
                            {{ session.agent.platform }} - {{ session.agent.browser }}
                        </div>

                        <div>
                            <div class="text-xs text-gray-500">
                                {{ session.ip_address }},

                                <span class="text-green-500 font-semibold" v-if="session.is_current_device">This device</span>
                                <span v-else>Last active {{ session.last_active }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex items-center mt-5">
                <jet-button class="primary" @click.native="confirmLogout">
                    <span class="text-white">Logout Other Browser Sessions</span>
                </jet-button>

                <jet-action-message :on="form.recentlySuccessful" class="ml-3">
                    Done.
                </jet-action-message>
            </div>

            <!-- Logout Other Devices Confirmation Modal -->
            <jet-dialog-modal :show="confirmingLogout" @close="confirmingLogout = false">
                <template #title>
                    Logout Other Browser Sessions
                </template>

                <template #content>
                    Please enter your password to confirm you would like to logout of your other browser sessions across all of your devices.

                    <div class="mt-4">
                        <jet-input type="password" class="mt-1 block w-3/4" placeholder="Password"
                                    ref="password"
                                    v-model="form.password"
                                    @keyup.enter.native="logoutOtherBrowserSessions" />

                        <jet-input-error :message="fieldError('password')" class="mt-2" />
                    </div>
                </template>

                <template #footer>
                    <jet-secondary-button @click.native="confirmingLogout = false">
                        Nevermind
                    </jet-secondary-button>

                    <jet-button class="ml-2 primary" @click.native="logoutOtherBrowserSessions" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        <span class="text-white">Logout Other Browser Sessions</span>
                    </jet-button>
                </template>
            </jet-dialog-modal>
        </template>
    </jet-action-section>
</template>

<script>
import JetActionMessage from '@/Jetstream/ActionMessage'
import JetActionSection from '@/Jetstream/ActionSection'
import JetButton from '@/Jetstream/Button'
import JetDialogModal from '@/Jetstream/DialogModal'
import JetInput from '@/Jetstream/Input'
import JetInputError from '@/Jetstream/InputError'
import JetSecondaryButton from '@/Jetstream/SecondaryButton'

export default {
   props: ['vSessions'],

   components: {
      JetActionMessage,
      JetActionSection,
      JetButton,
      JetDialogModal,
      JetInput,
      JetInputError,
      JetSecondaryButton,
   },

   data() {
         return {
            confirmingLogout: false,

         form: {
            '_method': 'DELETE',
            password: '',
            processing: false,
            recentlySuccessful: false,
         },
         formErrors: {},
      }
   },

   methods: {
      fieldError(field) {
         return this.formErrors[field] || '';
      },

      confirmLogout() {
         this.form.password = ''
         this.formErrors = {}

         this.confirmingLogout = true

         setTimeout(() => {
            this.$refs.password.focus()
         }, 250)
      },

      logoutOtherBrowserSessions() {
         this.form.processing = true
         this.form.recentlySuccessful = false
         this.formErrors = {}

         this.$inertia.post(route('other-browser-sessions.destroy'), {
            _method: 'DELETE',
            password: this.form.password,
         }, {
            preserveScroll: true,
            onError: (errors) => {
               this.formErrors = errors || {}
            },
            onSuccess: () => {
               this.form.recentlySuccessful = true
               this.confirmingLogout = false
               this.form.password = ''
            },
            onFinish: () => {
               this.form.processing = false
            },
         })
      },

      formError(field) {
         if (this.form && typeof this.form.error === 'function') {
            return this.form.error(field)
         }

         return (this.form && this.form.errors && this.form.errors[field]) || null
      },
   },

   computed: {
      safeSessions() {
         return Array.isArray(this.vSessions) ? this.vSessions : []
      },
   },

}
</script>
